import { generatePath, To } from 'react-router-dom'

import { Table, usePagination } from 'components/Table'
import { PaginationProps } from 'components/Table/Pagination'
import { useSort } from 'components/Table/Sort'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { calculatePagination } from 'providers/graphql'
import { ROUTES } from 'routes'
import { DEFAULT_SORT, SORTABLE_COLUMNS } from './constants'
import { useGetPages } from './hooks/useGetPages'
import { PageForTable, PageListProps, PageSortBy } from './types'
import { useColumns } from './useColumns'

export const PagesTable = ({ filters }: PageListProps): JSX.Element => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const columns = useColumns()
  const { page, rowsPerPage, setPage, ...usePaginationProps } = usePagination({
    resetOnChange: [filters],
  })
  const { props: sortProps, sort } = useSort<PageForTable, PageSortBy>(
    DEFAULT_SORT,
    SORTABLE_COLUMNS,
  )

  const { count, loading, pages } = useGetPages({
    filters,
    ...calculatePagination({ page, rowsPerPage }),
    sort,
  })

  const handleRowClick = (row: PageForTable): To =>
    generatePath(ROUTES.INVENTORY.PAGE.EDIT, {
      id: row.id,
      tab: 'general',
      workspaceId,
    })

  const paginationProps: PaginationProps = {
    count,
    page,
    rowsPerPage,
    setPage,
    ...usePaginationProps,
  }

  const canAccessEdit: boolean = ability.can('access', 'PagePage-edit')

  return (
    <Table<PageForTable>
      columns={columns}
      data={pages}
      loading={loading}
      meta={{ stickyHeader: true }}
      onClickRow={canAccessEdit ? id => handleRowClick(id) : undefined}
      pagination={paginationProps}
      sort={sortProps}
    />
  )
}
